import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  SubjectOption,
  useSubjectSearch,
} from "@/helper/customHooks/useSubjectSearch";
import { api } from "@/helper/api";
import { RootState } from "@/components/state/store";
import { LmsMode } from "@/components/lms/lms.utils";
import {
  getLmsSelectionBlockedMessage,
  LMS_START_VERTRAG_SELECTION_ENRICHMENT,
} from "@/components/lms/lmsQuickSearchEligibility";

export const KNOWN_ASSIGNMENT_TYPES = [
  "multiple_participants",
  "multiple_departments",
  "multiple_companies",
] as const;

export type KnownAssignmentType = (typeof KNOWN_ASSIGNMENT_TYPES)[number];

export const ASSIGNMENT_RULES_EXCLUDED_TYPES: KnownAssignmentType[] = [
  "multiple_participants",
];

const knownAssignmentTypeSet = new Set<string>(KNOWN_ASSIGNMENT_TYPES);

const getDepartmentRequestOverrides = (
  companySubjectIds: string[],
  minSearchLength?: number
) => ({
  ...(minSearchLength === undefined ? {} : { minSearchLength }),
  entity: "department",
  ...(companySubjectIds.length > 0
    ? {
        filterParameter: {
          company_subject_id__in: companySubjectIds,
        },
      }
    : {}),
  mapResponseToOptions: (data: any) =>
    (data?.results ?? []).map((department: any) => ({
      value: String(department.id),
      label: department.name,
      company_subject: department?.company_subject,
    })),
});

export const isKnownAssignmentType = (
  type: string
): type is KnownAssignmentType => knownAssignmentTypeSet.has(type);

interface GetAssignmentTypeLabelOptions {
  usesManagerCompanyScope?: boolean;
}

export interface AssignmentTypeOption {
  value: KnownAssignmentType;
  label: string;
}

export const getAssignmentTypeLabel = (
  type: string | null | undefined,
  t: (key: string) => string,
  options: GetAssignmentTypeLabelOptions = {}
): string => {
  if (type === "multiple_participants") return t("persons");
  if (type === "multiple_departments") return t("departments");
  if (type === "multiple_companies") {
    return options.usesManagerCompanyScope
      ? t("entire_company")
      : t("companies");
  }
  if (!type) return "-";

  return type
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const getAssignmentTypeOptions = (
  t: (key: string) => string,
  options: GetAssignmentTypeLabelOptions & { types?: string[] } = {}
): AssignmentTypeOption[] => {
  const types: string[] = options.types ?? [...KNOWN_ASSIGNMENT_TYPES];

  return types.filter(isKnownAssignmentType).map((type) => ({
    value: type,
    label: getAssignmentTypeLabel(type, t, options),
  }));
};

interface AssignmentTypeCompanySelections {
  participantCompanies: SubjectOption[];
  departmentCompanies: SubjectOption[];
  companies: SubjectOption[];
}

export const getCompanySelectionForAssignmentType = (
  assignmentType: string,
  selections: AssignmentTypeCompanySelections
): SubjectOption[] => {
  if (assignmentType === "multiple_participants") {
    return selections.participantCompanies;
  }

  if (assignmentType === "multiple_departments") {
    return selections.departmentCompanies;
  }

  if (assignmentType === "multiple_companies") {
    return selections.companies;
  }

  return [];
};

export const getPreservedCompanyOnAssignmentTypeChange = (
  fromType: string,
  selections: AssignmentTypeCompanySelections
): SubjectOption | null => {
  const selectedCompanies = getCompanySelectionForAssignmentType(
    fromType,
    selections
  );

  return selectedCompanies.length === 1 ? selectedCompanies[0] : null;
};

export const normalizeSubjectOptions = (items: any[] = []): SubjectOption[] =>
  items.map((item: any) => ({
    value: String(item?.id ?? item?.subject_id ?? item?.value ?? item),
    label: String(item?.name ?? item?.label ?? item?.id ?? item ?? ""),
    is_deleted: item?.is_deleted,
    is_active: item?.is_active,
    is_parent: item?.is_parent,
  }));

export const getAssignmentEnrolleeSet = (assignment: any): any[] => {
  if (assignment?.type === "multiple_participants") {
    return assignment.participant_set ?? [];
  }

  if (assignment?.type === "multiple_departments") {
    return assignment.department_set ?? [];
  }

  if (assignment?.type === "multiple_companies") {
    return assignment.company_subject_set ?? [];
  }

  return [];
};

export const getAssignmentEnrolleeNamePresentation = (
  assignment: any,
  maxVisible = 5
): { visibleNames: string[]; remainingCount: number } => {
  const names = getAssignmentEnrolleeSet(assignment)
    .map((item) => String(item?.name ?? "").trim())
    .filter(Boolean);

  const visibleNames = names.slice(0, maxVisible);
  const remainingCount = Math.max(0, names.length - visibleNames.length);

  return { visibleNames, remainingCount };
};

export const getAssignmentCompanyNamePresentation = (
  assignment: any,
  maxVisible = 5
): { visibleNames: string[]; remainingCount: number } => {
  const names = (assignment?.associated_company_subject_set ?? [])
    .map((item: any) => String(item?.name ?? "").trim())
    .filter(Boolean);

  const visibleNames = names.slice(0, maxVisible);
  const remainingCount = Math.max(0, names.length - visibleNames.length);

  return { visibleNames, remainingCount };
};

const getDepartmentCompanyOptionFromAssignment = (assignment: any) => {
  const firstDepartmentCompany = assignment?.department_set?.find(
    (department: any) => department?.company_subject
  )?.company_subject;

  if (firstDepartmentCompany) {
    return {
      value: String(
        firstDepartmentCompany.subject_id ?? firstDepartmentCompany.id
      ),
      label: String(
        firstDepartmentCompany.name ?? firstDepartmentCompany.label ?? ""
      ),
    };
  }

  const firstEnrolmentCompany = assignment?.enrolment_set?.find(
    (enrolment: any) => enrolment?.participant?.company
  )?.participant?.company;

  if (firstEnrolmentCompany) {
    return {
      value: String(
        firstEnrolmentCompany.subject_id ?? firstEnrolmentCompany.id
      ),
      label: String(
        firstEnrolmentCompany.name ?? firstEnrolmentCompany.label ?? ""
      ),
    };
  }

  return null;
};

export const getParticipantCompanyOptionFromAssignment = (assignment: any) =>
  getDepartmentCompanyOptionFromAssignment(assignment);

export const resolveManagerCompanyFromMeResponse = (
  data: any,
  fallbackCompanyName = ""
): SubjectOption | null => {
  const companyName = String(
    data?.company_name || fallbackCompanyName || ""
  ).trim();
  if (!companyName) {
    return null;
  }

  if (data?.subject_id) {
    return {
      value: String(data.subject_id),
      label: companyName,
    };
  }

  const assignedCompanies = data?.assigned_companies;
  if (assignedCompanies && typeof assignedCompanies === "object") {
    for (const entry of Object.values(assignedCompanies) as any[]) {
      const company = entry?.company_subject ?? entry?.company;
      const companySubjectId = company?.subject_id ?? company?.id;
      if (!companySubjectId) {
        continue;
      }
      if (String(company.name) === companyName) {
        return {
          value: String(companySubjectId),
          label: String(company.name),
        };
      }
    }

    for (const entry of Object.values(assignedCompanies) as any[]) {
      const company = entry?.company_subject ?? entry?.company;
      const companySubjectId = company?.subject_id ?? company?.id;
      if (companySubjectId) {
        return {
          value: String(companySubjectId),
          label: String(company.name ?? companyName),
        };
      }
    }
  }

  return null;
};

interface Props {
  selectedAssignmentType: string;
  mode?: LmsMode;
  subdomain?: string;
  selectedCompanySubjectId?: string | number;
  selectedCompanyName?: string;
}

const resolveEffectiveLmsMode = (mode: LmsMode, subdomain?: string): LmsMode =>
  subdomain === "manage" ? "manager" : mode;

export const useEnrolmentAssignmentTargetSelection = ({
  selectedAssignmentType,
  mode = "administrator",
  subdomain,
  selectedCompanySubjectId,
  selectedCompanyName,
}: Props) => {
  const { t } = useTranslation();
  const effectiveMode = resolveEffectiveLmsMode(mode, subdomain);
  const loginCompanyName = useSelector(
    (state: RootState) => state.login.companyName
  );
  const [managerLockedCompanyOption, setManagerLockedCompanyOption] =
    useState<SubjectOption | null>(null);

  const { fetchSubjects } = useSubjectSearch({
    subdomain: effectiveMode === "manager" ? "manage" : "admin",
    allowSubjectSearchForNonAdmin: effectiveMode === "manager",
    subjectIdForFiltering:
      effectiveMode === "manager" && selectedCompanySubjectId
        ? String(selectedCompanySubjectId)
        : undefined,
  });

  const [selectedParticipants, setSelectedParticipants] = useState<
    SubjectOption[]
  >([]);
  const [selectedDepartments, setSelectedDepartments] = useState<
    SubjectOption[]
  >([]);
  const [selectedDepartmentCompanies, setSelectedDepartmentCompanies] =
    useState<SubjectOption[]>([]);
  const [selectedParticipantCompanies, setSelectedParticipantCompanies] =
    useState<SubjectOption[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<SubjectOption[]>(
    []
  );
  const [selectedFallbackTargets, setSelectedFallbackTargets] = useState<
    SubjectOption[]
  >([]);

  const resetSelections = useCallback(() => {
    setSelectedParticipants([]);
    setSelectedDepartments([]);
    setSelectedDepartmentCompanies([]);
    setSelectedParticipantCompanies([]);
    setSelectedCompanies([]);
    setSelectedFallbackTargets([]);
  }, []);

  const initializeSelectionsFromAssignment = useCallback((assignment: any) => {
    setSelectedParticipants(
      normalizeSubjectOptions(assignment?.participant_set)
    );
    setSelectedDepartments(normalizeSubjectOptions(assignment?.department_set));
    setSelectedCompanies(
      normalizeSubjectOptions(assignment?.company_subject_set)
    );
    setSelectedFallbackTargets([]);

    const departmentCompany =
      getDepartmentCompanyOptionFromAssignment(assignment);
    setSelectedDepartmentCompanies(
      departmentCompany ? [departmentCompany] : []
    );

    const participantCompany =
      getParticipantCompanyOptionFromAssignment(assignment);
    setSelectedParticipantCompanies(
      participantCompany ? [participantCompany] : []
    );
  }, []);

  const selectedTargetOptions =
    selectedAssignmentType === "multiple_participants"
      ? selectedParticipants
      : selectedAssignmentType === "multiple_departments"
      ? selectedDepartments
      : selectedAssignmentType === "multiple_companies"
      ? selectedCompanies
      : selectedFallbackTargets;

  const handleGenericTargetChange = (value: any) => {
    const nextValue = value ?? [];
    if (selectedAssignmentType === "multiple_participants") {
      setSelectedParticipants(nextValue);
      return;
    }
    if (selectedAssignmentType === "multiple_companies") {
      setSelectedCompanies(nextValue);
      return;
    }
    setSelectedFallbackTargets(nextValue);
  };

  const applyAssignmentTypeChange = useCallback(
    (fromType: string, toType: string) => {
      if (fromType === toType) {
        return;
      }

      const preservedCompany = getPreservedCompanyOnAssignmentTypeChange(
        fromType,
        {
          participantCompanies: selectedParticipantCompanies,
          departmentCompanies: selectedDepartmentCompanies,
          companies: selectedCompanies,
        }
      );

      setSelectedParticipants([]);
      setSelectedDepartments([]);
      setSelectedParticipantCompanies([]);
      setSelectedDepartmentCompanies([]);
      setSelectedCompanies([]);
      setSelectedFallbackTargets([]);

      if (!preservedCompany || !isKnownAssignmentType(toType)) {
        return;
      }

      if (toType === "multiple_participants") {
        setSelectedParticipantCompanies([preservedCompany]);
        return;
      }

      if (toType === "multiple_departments") {
        setSelectedDepartmentCompanies([preservedCompany]);
        return;
      }

      setSelectedCompanies([preservedCompany]);
    },
    [
      selectedCompanies,
      selectedDepartmentCompanies,
      selectedParticipantCompanies,
    ]
  );

  const getManagerCompanySubjectId = () =>
    effectiveMode === "manager" && selectedCompanySubjectId
      ? String(selectedCompanySubjectId)
      : undefined;

  const getParticipantCompanySubjectId = () => {
    if (selectedParticipantCompanies.length > 0) {
      return String(selectedParticipantCompanies[0].value);
    }

    return getManagerCompanySubjectId();
  };

  const departmentCompanySubjectIds = useMemo(() => {
    if (selectedDepartmentCompanies.length > 0) {
      return selectedDepartmentCompanies.map((company) =>
        String(company.value)
      );
    }

    const managerCompanySubjectId =
      effectiveMode === "manager" && selectedCompanySubjectId
        ? String(selectedCompanySubjectId)
        : undefined;
    return managerCompanySubjectId ? [managerCompanySubjectId] : [];
  }, [effectiveMode, selectedCompanySubjectId, selectedDepartmentCompanies]);
  const departmentOptionsScopeKey = departmentCompanySubjectIds.join("|");

  const loadParticipantOptions = (input: string) => {
    const companySubjectId = getParticipantCompanySubjectId();
    if (effectiveMode !== "manager" && !companySubjectId) {
      return Promise.resolve([]);
    }

    return fetchSubjects(input, {
      queryParameter: "users",
      entity: "subject/quicksearch",
      ...(companySubjectId ? { subjectIdForFiltering: companySubjectId } : {}),
      mapResponseToOptions: (data: any) =>
        (data?.users ?? [])
          .filter((user: any) => user.subject_id)
          .map((user: any) => ({
            value: user.subject_id,
            label: `${user.name} (${user.email} - ${user.company_name})`,
            is_deleted: user.is_deleted,
            selection: user.selection,
          })),
      selectionEnrichment: LMS_START_VERTRAG_SELECTION_ENRICHMENT,
    });
  };

  const loadCompanyOptions = (input: string) =>
    fetchSubjects(input, {
      queryParameter: "companies",
      entity: "subject/quicksearch",
      subjectIdForFiltering:
        effectiveMode === "manager" && selectedCompanySubjectId
          ? String(selectedCompanySubjectId)
          : undefined,
      mapResponseToOptions: (data: any) =>
        (data?.companies ?? [])
          .filter((company: any) => company.subject_id)
          .map((company: any) => ({
            value: String(company.subject_id),
            label: company.name,
            is_active: company.is_active,
            is_parent: company.is_parent,
            selection: company.selection,
          })),
      selectionEnrichment: LMS_START_VERTRAG_SELECTION_ENRICHMENT,
    });

  const loadDepartmentOptions = (input: string) => {
    if (
      effectiveMode !== "manager" &&
      departmentCompanySubjectIds.length === 0
    ) {
      return Promise.resolve([]);
    }

    return fetchSubjects(
      input,
      getDepartmentRequestOverrides(departmentCompanySubjectIds)
    );
  };

  const loadAllDepartmentOptions = useCallback(() => {
    if (
      effectiveMode !== "manager" &&
      departmentCompanySubjectIds.length === 0
    ) {
      return Promise.resolve([]);
    }

    return fetchSubjects(
      "",
      getDepartmentRequestOverrides(departmentCompanySubjectIds, 0)
    );
  }, [departmentCompanySubjectIds, effectiveMode, fetchSubjects]);

  const loadFallbackSubjectOptions = (input: string) =>
    fetchSubjects(input, {
      entity: "subject/quicksearch",
      selectionEnrichment: LMS_START_VERTRAG_SELECTION_ENRICHMENT,
      subjectIdForFiltering:
        effectiveMode === "manager" && selectedCompanySubjectId
          ? String(selectedCompanySubjectId)
          : undefined,
    });

  const getSelectionBlockedMessage = useCallback(
    (selection: any) => getLmsSelectionBlockedMessage(selection, t),
    [t]
  );

  const buildAssignmentTargetPayload = () => {
    const selectedTargetIds = selectedTargetOptions.map((target) =>
      String(target.value)
    );

    if (!isKnownAssignmentType(selectedAssignmentType)) {
      return { target_ids: selectedTargetIds };
    }

    if (selectedAssignmentType === "multiple_participants") {
      return { participant_set: selectedTargetIds };
    }
    if (selectedAssignmentType === "multiple_departments") {
      return { department_set: selectedTargetIds };
    }
    return { company_subject_set: selectedTargetIds };
  };

  const usesManagerCompanyScope = effectiveMode === "manager";

  useEffect(() => {
    if (!usesManagerCompanyScope) {
      setManagerLockedCompanyOption(null);
      return;
    }

    if (selectedCompanySubjectId) {
      setManagerLockedCompanyOption({
        value: String(selectedCompanySubjectId),
        label:
          selectedCompanyName ||
          loginCompanyName ||
          String(selectedCompanySubjectId),
      });
      return;
    }

    api.genericApiRequest({
      entity: "me",
      method: "get",
      successHandler: (response: any) => {
        setManagerLockedCompanyOption(
          resolveManagerCompanyFromMeResponse(response?.data, loginCompanyName)
        );
      },
      failHandler: () => {
        if (loginCompanyName) {
          setManagerLockedCompanyOption(null);
        }
      },
    });
  }, [
    loginCompanyName,
    selectedCompanyName,
    selectedCompanySubjectId,
    usesManagerCompanyScope,
  ]);

  useEffect(() => {
    if (selectedAssignmentType !== "multiple_companies") {
      return;
    }

    if (!managerLockedCompanyOption?.value) {
      return;
    }

    setSelectedCompanies([managerLockedCompanyOption]);
  }, [managerLockedCompanyOption, selectedAssignmentType]);

  const managerLockedCompanyDisplayName =
    managerLockedCompanyOption?.label ||
    selectedCompanyName ||
    loginCompanyName ||
    "";

  return {
    usesManagerCompanyScope,
    managerLockedCompanyOption,
    managerLockedCompanyDisplayName,
    selectedTargetOptions,
    selectedDepartments,
    selectedDepartmentCompanies,
    selectedParticipantCompanies,
    setSelectedDepartments,
    setSelectedDepartmentCompanies,
    setSelectedParticipantCompanies,
    setSelectedParticipants,
    setSelectedCompanies,
    handleGenericTargetChange,
    loadParticipantOptions,
    loadCompanyOptions,
    loadDepartmentOptions,
    loadAllDepartmentOptions,
    departmentOptionsScopeKey,
    loadFallbackSubjectOptions,
    getSelectionBlockedMessage,
    buildAssignmentTargetPayload,
    resetSelections,
    initializeSelectionsFromAssignment,
    applyAssignmentTypeChange,
  };
};
